import React from 'react';
import { Box, Button, ButtonGroup } from '@mui/material';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { DateInputField } from '../DateInputField';

export interface ReportDateRange {
  startDate: string;
  endDate: string;
}

interface ReportDateRangeFilterProps {
  value: ReportDateRange;
  onChange: (range: ReportDateRange) => void;
  minimumStartDate?: string;
}

export const ReportDateRangeFilter: React.FC<ReportDateRangeFilterProps> = ({
  value,
  onChange,
  minimumStartDate,
}) => {
  const { t } = useTranslation();
  const resolvedMinimumStartDate =
    minimumStartDate && dayjs(minimumStartDate).isValid()
      ? dayjs(minimumStartDate).format('YYYY-MM-DD')
      : '2000-01-01';
  const presets: { labelKey: string; labelDefault: string; range: () => ReportDateRange }[] = [
    {
      labelKey: 'reports.filters.thisMonth',
      labelDefault: 'This Month',
      range: () => ({
        startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
        endDate: dayjs().endOf('month').format('YYYY-MM-DD'),
      }),
    },
    {
      labelKey: 'reports.filters.lastMonth',
      labelDefault: 'Last Month',
      range: () => ({
        startDate: dayjs().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
        endDate: dayjs().subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
      }),
    },
    {
      labelKey: 'reports.filters.thisYear',
      labelDefault: 'This Year',
      range: () => ({
        startDate: dayjs().startOf('year').format('YYYY-MM-DD'),
        endDate: dayjs().endOf('year').format('YYYY-MM-DD'),
      }),
    },
    {
      labelKey: 'reports.filters.allTime',
      labelDefault: 'All Time',
      range: () => ({
        startDate: resolvedMinimumStartDate,
        endDate: dayjs().endOf('day').format('YYYY-MM-DD'),
      }),
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 3 }}>
      <DateInputField
        label={t('reports.filters.startDate', 'Start Date')}
        size="small"
        value={value.startDate}
        onChange={(startDate) => onChange({ ...value, startDate })}
        minDate={resolvedMinimumStartDate}
        maxDate={value.endDate || undefined}
      />
      <DateInputField
        label={t('reports.filters.endDate', 'End Date')}
        size="small"
        value={value.endDate}
        onChange={(endDate) => onChange({ ...value, endDate })}
        minDate={value.startDate || resolvedMinimumStartDate}
        maxDate={dayjs().format('YYYY-MM-DD')}
      />
      <ButtonGroup size="small" variant="outlined">
        {presets.map((preset) => (
          <Button key={preset.labelKey} onClick={() => onChange(preset.range())}>
            {t(preset.labelKey, preset.labelDefault)}
          </Button>
        ))}
      </ButtonGroup>
    </Box>
  );
};
